import makeUseStyles from "@assets/style/util/makeUseStyles"
import DiscoDropdownItem from "@disco-ui/dropdown/DiscoDropdownItem"
import DiscoMoreActionsDropdown from "@disco-ui/dropdown/DiscoMoreActionsDropdown"
import useDisclosure from "@utils/hook/useDisclosure"
import { formatDateWithOptions } from "@utils/time/timeUtils"
import { TestIDProps } from "@utils/typeUtils"
import { graphql, useFragment } from "react-relay"
import DeleteTrainingSourceButton from "./DeleteTrainingSourceButton"
import RecrawlTrainingSourceButton from "./RecrawlTrainingSourceButton"
import TrainingDataItems from "./TrainingDataItems"
import TrainingSourceSettingsButton from "./TrainingSourceSettingsButton"
import TrainingSourceTableItem from "./TrainingSourceTableItem"
import { TrainingSourceTableListItemFragment$key } from "./__generated__/TrainingSourceTableListItemFragment.graphql"

interface Props extends TestIDProps {
  trainingSourceKey: TrainingSourceTableListItemFragment$key
}

export default function TrainingSourceTableListItem({
  testid = "TrainingSourceTableListItem",
  trainingSourceKey,
}: Props) {
  const { isOpen, onToggle } = useDisclosure()
  const classes = useStyles()
  const source = useFragment<TrainingSourceTableListItemFragment$key>(
    graphql`
      fragment TrainingSourceTableListItemFragment on TrainingSource {
        id
        name
        status
        kind
        createdAt
        url
        trainingData {
          totalCount
        }
        ...TrainingSourceSettingsFormFragment
      }
    `,
    trainingSourceKey
  )

  return (
    <div>
      <TrainingSourceTableItem
        name={source.name || source.url}
        url={source.url}
        dateCreated={formatDateWithOptions()(new Date(source.createdAt))}
        pages={source.trainingData.totalCount}
        status={source.status}
        onClick={expandRow}
        overflow={
          <DiscoMoreActionsDropdown testid={testid} rotateIcon>
            {/* Recrawl */}
            <RecrawlTrainingSourceButton trainingSourceId={source.id}>
              {(props) => (
                <DiscoDropdownItem
                  {...props}
                  icon={"refresh"}
                  title={"Sync"}
                  classes={{
                    icon: classes.icon,
                  }}
                />
              )}
            </RecrawlTrainingSourceButton>
            {/* Settings*/}
            <TrainingSourceSettingsButton trainingSourceKey={source} kind={source.kind}>
              {(props) => (
                <DiscoDropdownItem
                  {...props}
                  icon={"edit"}
                  title={"Edit"}
                  classes={{
                    icon: classes.icon,
                  }}
                />
              )}
            </TrainingSourceSettingsButton>

            <DeleteTrainingSourceButton trainingSourceId={source.id} kind={source.kind}>
              {(props) => (
                <DiscoDropdownItem
                  {...props}
                  icon={"trash"}
                  title={"Delete"}
                  classes={{
                    icon: classes.icon,
                  }}
                />
              )}
            </DeleteTrainingSourceButton>
          </DiscoMoreActionsDropdown>
        }
      />

      {isOpen && source.trainingData.totalCount > 1 && (
        <TrainingDataItems trainingSourceId={source.id} />
      )}
    </div>
  )

  function expandRow() {
    if (source.trainingData.totalCount === 1) return
    onToggle()
  }
}

const useStyles = makeUseStyles({
  icon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
})
